import { useTranslation } from "react-i18next";
import { Form } from "semantic-ui-react";

import { Can, DATE_FORMATS_FNS, UploadFilesZone, UploadedFilesPreview } from "components";
import ENV from "config/Env";
import { usePermanentRealTimeUser, useUploadAttachment } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { getFilesWithinSizeLimit } from "utils/common";
import { formatCurrentUTCDate, formatDate } from "utils/dateHelperFunctions";

import type { PreviewAttachment } from "components";
import type { Customer, CustomerNoteAttachment, DealerLocation } from "models";
import type { Dispatch, SetStateAction } from "react";

type CustomerNoteModalAttachmentProps = {
  customer: Customer;
  attachments: CustomerNoteAttachment[] | null;
  location?: DealerLocation;
  setAttachments: Dispatch<SetStateAction<CustomerNoteAttachment[] | null>>;
  onUpload?: (isUploading: boolean) => void;
};

export const CustomerNoteModalAttachments = ({ customer, location, attachments, setAttachments, onUpload }: CustomerNoteModalAttachmentProps) => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { handleUploadFileAttachment, isUploadingAttachment } = useUploadAttachment("/notes/attachments/upload", ENV.carBaseURL, onUpload);

  const uploadedAttachmentsPreview: PreviewAttachment[] =
    attachments?.map(({ url, name, created_on, created_by_id, username }) => ({ url, name, created_on, user_id: created_by_id, username })) || [];

  const handleUploadAttachment = async (file: File) => {
    const extension = file.name.lastIndexOf(".") > -1 ? file.name.slice(file.name.lastIndexOf(".") + 1) : "unknown";
    const uploadFile = new File([file], file.name, { type: file.type || extension });
    const formData = new FormData();

    const date = formatDate(customer.created_on, DATE_FORMATS_FNS.yearMonthDateSlash);
    const path = `${location?.id}_${location?.name}/${date}/${customer.id}`;

    formData.append("file", uploadFile);
    formData.append("path", path);

    const attachmentUrl = await handleUploadFileAttachment({ formData });
    if (attachmentUrl) {
      const newFile: CustomerNoteAttachment = {
        url: attachmentUrl,
        type: file.type || extension,
        name: file.name,
        created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
        username: `${user?.first_name || ""} ${user?.last_name || ""}`,
        customer_note_id: -1,
        id: -1,
        created_by_id: user?.id ?? 0
      };
      setAttachments(attach => (attach ? [...attach, newFile] : [newFile]));
    }
  };

  const handleDeleteAttachment = (url: string) => {
    if (attachments?.length) setAttachments(attach => (attach ? attach.filter(attachment => attachment.url !== url) : null));
  };

  const handleFileChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const files = getFilesWithinSizeLimit(Array.from(evt.target.files ?? []), t);

    files.forEach(file => handleUploadAttachment(file));
  };

  const handleFileDragOver = (evt: React.DragEvent<HTMLInputElement>) => evt.preventDefault();

  const handleFileDrop = (evt: React.DragEvent<HTMLInputElement>) => {
    evt.preventDefault();
    const files = getFilesWithinSizeLimit(Array.from(evt.dataTransfer.files), t);

    files.forEach(file => handleUploadAttachment(file));
  };

  return (
    <>
      <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.CustomerNoteAttachments}>
        <Form.Field>
          <UploadFilesZone
            hasError={false}
            display={true}
            isUploading={isUploadingAttachment}
            onFileInputChange={handleFileChange}
            onFileDragOver={handleFileDragOver}
            onFileDrop={handleFileDrop}
          />
        </Form.Field>
      </Can>
      <UploadedFilesPreview attachments={uploadedAttachmentsPreview} onDeleteAttachment={handleDeleteAttachment} />
    </>
  );
};
